// src/features/deadlines/components/CustomMessageInput.tsx

import React, { useState, useRef, useEffect } from 'react';
import { Wand2, AlertCircle, Check, RefreshCw, Sparkles, Eye } from 'lucide-react';
import { textOptimizationService } from '../../../services/textOptimization/TextOptimizationService';
import { EmailPreviewModal } from './EmailPreviewModal';
import { notificationService } from '../../../services/notification/notificationService';
import { Customer } from '../../../shared/types';
import { EmailSenderIdentity, EmailPreview } from '../../../services/email/types';

interface CustomMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onOptimized?: (optimizedText: string) => void;
  // Props für E-Mail-Kontext
  customer?: Customer;
  month?: number;
  year?: number;
  senderIdentity?: EmailSenderIdentity;
}

export const CustomMessageInput: React.FC<CustomMessageInputProps> = ({
  value,
  onChange,
  onOptimized,
  customer,
  month,
  year,
  senderIdentity
}) => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedText, setOptimizedText] = useState<string | null>(null);
  const [originalText, setOriginalText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showOptimized, setShowOptimized] = useState(false);
  const [hasOptimized, setHasOptimized] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Preview-Funktionalität
  const [showPreview, setShowPreview] = useState(false);
  const [emailPreview, setEmailPreview] = useState<EmailPreview | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Auto-resize textarea basierend auf Inhalt
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 80;
      const maxHeight = 200;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [value, optimizedText, showOptimized]);

  const handleOptimize = async () => {
    if (!value.trim()) {
      setError('Bitte geben Sie einen Text ein, der optimiert werden soll.');
      return;
    }

    if (hasOptimized && optimizedText === value) {
      setError('Der Text wurde bereits optimiert. Bitte nehmen Sie erst Änderungen vor.');
      return;
    }

    setIsOptimizing(true);
    setError(null);
    
    // Speichere den Original-Text VOR der Optimierung
    setOriginalText(value);

    try {
      let result;
      
      // ERWEITERT: Nutze E-Mail-Kontext falls verfügbar (aber ohne UI-Änderungen)
      if (customer && month && year) {
        // Erstelle E-Mail-Variablen für Kontext
        const monthNames = [
          'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
          'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
        ];

        const emailVariables = {
          customerName: customer.company,
          customerNumber: customer.mandantennummer || 'TEST',
          month: monthNames[month - 1],
          monthNumber: month,
          year,
          nachname: customer.nachname || undefined,
          vorname: customer.vorname || undefined,
          anrede: customer.anrede as 'Du' | 'Sie' | undefined,
          geschlecht: customer.geschlecht as 'männlich' | 'weiblich' | 'divers' | undefined,
          senderIdentity: senderIdentity,
          imAuftragVon: customer.im_auftrag_von || undefined,
          customMessage: value, // Aktueller Text
          isDuoNotification: customer.versandmethode === 'duo',
          customerEmail: customer.email
        };

        // Nutze erweiterte Optimierung mit E-Mail-Kontext
        result = await textOptimizationService.optimizeTextWithEmailContext(
          value,
          emailVariables
        );
      } else {
        // Fallback zu standalone-Optimierung
        result = await textOptimizationService.optimizeTextStandalone(value);
      }
      
      if (result.success && result.optimizedText) {
        setOptimizedText(result.optimizedText);
        setShowOptimized(true);
        setHasOptimized(true);
        
        // Automatisch den optimierten Text übernehmen
        onChange(result.optimizedText);
        
        if (onOptimized) {
          onOptimized(result.optimizedText);
        }
      } else {
        setError(result.error || 'Textoptimierung fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler bei der Verbindung zum Optimierungsservice');
      console.error('Optimization error:', err);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleRevert = () => {
    if (originalText) {
      onChange(originalText);
      setOptimizedText(null);
      setHasOptimized(false);
      setShowOptimized(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!customer || !month || !year) {
      setError('Vorschau kann nicht generiert werden - Kundendaten fehlen');
      return;
    }

    setIsGeneratingPreview(true);
    setError(null);

    try {
      const preview = await notificationService.generateEmailPreview(
        customer,
        month,
        year,
        senderIdentity,
        value // Aktuelle Custom Message
      );
      
      setEmailPreview(preview);
      setShowPreview(true);
    } catch (err) {
      setError('Fehler beim Generieren der Vorschau');
      console.error('Preview generation error:', err);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Zeige Preview-Button nur wenn Text vorhanden und Kundendaten verfügbar
  const canShowPreview = value.trim().length > 0 && customer && month && year;

  return (
    <>
      <div style={{
        backgroundColor: '#F8FAFC',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #E2E8F0',
        marginBottom: '16px'
      }}>
        {/* Header - ORIGINAL DESIGN */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Sparkles size={16} color="#6366F1" />
            Individuelle Nachricht (optional)
          </label>
          {hasOptimized && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '12px',
              color: '#10B981',
              backgroundColor: '#D1FAE5',
              padding: '4px 8px',
              borderRadius: '6px',
              gap: '4px'
            }}>
              <Check size={12} />
              Optimiert
            </span>
          )}
        </div>

        {/* Textarea Container - ORIGINAL DESIGN */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              onChange(newValue);
              setHasOptimized(false);
              setOptimizedText(null);
              setError(null);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Fügen Sie hier eine persönliche Nachricht für den Kunden hinzu..."
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: isFocused ? '2px solid #6366F1' : '2px solid #E2E8F0',
              borderRadius: '8px',
              resize: 'none',
              fontFamily: 'inherit',
              backgroundColor: hasOptimized ? '#F0FDF4' : '#FFFFFF',
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
              outline: 'none',
              lineHeight: '1.6',
              overflow: 'auto'
            }}
          />
        </div>

        {/* Error Message - ORIGINAL DESIGN */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#991B1B',
            marginBottom: '12px'
          }}>
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {/* Action Buttons Container - ORIGINAL DESIGN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Optimize Button - ORIGINAL DESIGN aber mit erweiterter Funktionalität */}
          <button
            type="button"
            onClick={handleOptimize}
            disabled={isOptimizing || !value.trim() || (hasOptimized && optimizedText === value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px',
              border: 'none',
              cursor: (isOptimizing || !value.trim() || (hasOptimized && optimizedText === value))
                ? 'not-allowed' : 'pointer',
              background: (isOptimizing || !value.trim() || (hasOptimized && optimizedText === value))
                ? '#E2E8F0'
                : 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)', // ORIGINAL GRADIENT
              color: (isOptimizing || !value.trim() || (hasOptimized && optimizedText === value))
                ? '#94A3B8'
                : '#FFFFFF',
              transition: 'all 0.2s ease'
            }}
          >
            {isOptimizing ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Text wird optimiert...
              </>
            ) : (
              <>
                <Wand2 size={16} />
                Text optimieren
              </>
            )}
          </button>

          {/* Preview Button - ORIGINAL DESIGN */}
          {canShowPreview && (
            <button
              type="button"
              onClick={handleGeneratePreview}
              disabled={isGeneratingPreview}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '8px',
                border: '2px solid #6366F1',
                cursor: isGeneratingPreview ? 'not-allowed' : 'pointer',
                backgroundColor: 'transparent',
                color: '#6366F1',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isGeneratingPreview) {
                  e.currentTarget.style.backgroundColor = '#EEF2FF';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {isGeneratingPreview ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Vorschau wird generiert...
                </>
              ) : (
                <>
                  <Eye size={16} />
                  E-Mail Vorschau anzeigen
                </>
              )}
            </button>
          )}
        </div>

        {/* Helper Text - ORIGINAL DESIGN */}
        <p style={{
          fontSize: '12px',
          color: '#64748B',
          textAlign: 'center',
          marginTop: '10px',
          marginBottom: '0'
        }}>
          💡 Die Optimierung korrigiert Rechtschreibung und Stil • Vorschau zeigt die komplette E-Mail
        </p>

        {/* Revert Button - ORIGINAL DESIGN */}
        {hasOptimized && originalText && originalText !== value && (
          <button
            type="button"
            onClick={handleRevert}
            style={{
              display: 'block',
              margin: '10px auto 0',
              padding: '6px 12px',
              fontSize: '12px',
              color: '#6366F1',
              backgroundColor: 'transparent',
              border: '1px solid #C7D2FE',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#EEF2FF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ↩ Ursprünglichen Text wiederherstellen
          </button>
        )}
      </div>

      {/* E-Mail Preview Modal */}
      {showPreview && emailPreview && (
        <EmailPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          preview={emailPreview}
        />
      )}
    </>
  );
};
